import { PRODUCTION_CONFIG } from './config/production';

// API Configuration - Production Ready
export const API_CONFIG = {
  baseUrl: PRODUCTION_CONFIG.API_URL,
  timeout: PRODUCTION_CONFIG.API_TIMEOUT,
  endpoints: {
    // Authentication endpoints
    register: "/api/auth/register",
    login: "/api/auth/login",
    
    // User endpoints
    getCurrentUser: "/api/users/me",
    
    // Event endpoints
    createEvent: "/api/events",
    getEvent: "/api/events/:eventId",
    getEvents: "/api/events",
    updateEvent: "/api/events/:eventId",
    deleteEvent: "/api/events/:eventId",
    
    // Registration endpoints
    registerUser: "/api/events/:eventId/register",
    getRegistrations: "/api/events/:eventId/registrations",
    getRegistrationFilters: "/api/events/:eventId/registrations/filters",
    getFilteredRegistrations: "/api/events/:eventId/registrations/filtered",
    updateRegistration: "/api/events/:eventId/registrations/:userId",
    checkinUser: "/api/events/:eventId/registrations/:userId/checkin",
    exportRegistrations: "/api/events/:eventId/registrations/export",
    
    // QR Scanner endpoints
    scanQR: "/api/events/:eventId/scan",
    
    // Payment endpoints
    approvePayment: "/api/events/:eventId/registrations/:userId/payment/approve",
    rejectPayment: "/api/events/:eventId/registrations/:userId/payment/reject",
    
    // Registration approval endpoints
    approveRegistration: "/api/events/:eventId/registrations/:userId/approve",
    rejectRegistration: "/api/events/:eventId/registrations/:userId/reject",
    cancelRegistration: "/api/events/:eventId/registrations/:userId/cancel",
    getRegistrationStatus: "/api/events/:eventId/registrations/:userId/status",
    bulkApproveRegistrations: "/api/events/:eventId/registrations/bulk-approve",
    bulkRejectRegistrations: "/api/events/:eventId/registrations/bulk-reject",
    bulkSendReminderEmails: "/api/events/:eventId/registrations/bulk-reminder",
    
    
    // Form Schema endpoints
    getFormSchemas: "/api/forms/schemas",
    saveFormSchema: "/api/forms/schemas",
    updateFormSchema: "/api/forms/schemas/:schemaId",
    deleteFormSchema: "/api/forms/schemas/:schemaId",
    
    // Payment Settings endpoints
    getPaymentSettings: "/api/payment/settings",
    getGlobalPaymentSettings: "/api/payment/settings/global",
    getEventPaymentSettings: "/api/payment/settings/event",
    getEffectivePaymentSettings: "/api/payment/settings/effective",
    savePaymentSettings: "/api/payment/settings",
    updatePaymentSettings: "/api/payment/settings",
    deletePaymentSettings: "/api/payment/settings",

    // Cashfree: platform account, Easy Split merchants, per-event config and checkout
    paymentMerchants: "/api/payment-merchants",
    eventPaymentConfig: "/api/events/:eventId/payment-config",
    createCashfreeOrderPublic: "/api/events/:eventId/cashfree/order",
    verifyCashfreeOrderPublic: "/api/events/:eventId/cashfree/orders/:orderId",

    
    // Email Configuration endpoints
    getEventEmailConfig: "/api/events/:eventId/emails",
    updateEventEmailConfig: "/api/events/:eventId/emails",

    // Locker endpoints
    configureLockers: "/api/events/:eventId/lockers/configure",
    importLockersCsv: "/api/events/:eventId/lockers/import-csv",
    availableLockers: "/api/events/:eventId/lockers/available",
    lockerSummary: "/api/events/:eventId/lockers/summary",
    lockersDashboard: "/api/events/:eventId/lockers",
    lockerNumberHistory: "/api/events/:eventId/lockers/:number/history",
    assignLockers: "/api/events/:eventId/registrations/:userId/lockers/assign",
    freeLockers: "/api/events/:eventId/registrations/:userId/lockers/free",
    getUserLockers: "/api/events/:eventId/registrations/:userId/lockers",
    getUserLockerHistory: "/api/events/:eventId/registrations/:userId/lockers/history",
    
    // Health endpoint
    health: "/api/health",

    applyCoupon: "/api/events/:eventId/coupons/apply",
    removeCoupon: "/api/events/:eventId/coupons/remove",
    listCoupons: "/api/coupons",
    createCoupon: "/api/coupons",
    getCoupon: "/api/coupons/:couponId",
    updateCoupon: "/api/coupons/:couponId",
    activateCoupon: "/api/coupons/:couponId/activate",
    deactivateCoupon: "/api/coupons/:couponId/deactivate",
    couponRedemptions: "/api/coupons/:couponId/redemptions",
  },
  fallback: false, // Production mode - no fallback data
  demoMode: false,
  retryAttempts: PRODUCTION_CONFIG.RETRY_ATTEMPTS,
  retryDelay: PRODUCTION_CONFIG.RETRY_DELAY
};

// QR Scan Purposes
export const QR_SCAN_PURPOSES = {
  badge: { name: "Badge Generation", description: "Generate access badge" },
  checkin: { name: "Event Check-in", description: "Check in to event" },
  lunch: { name: "Lunch Distribution", description: "Redeem lunch voucher" },
  swag: { name: "Swag Distribution", description: "Collect event swag" },
  kit: { name: "Kit Distribution", description: "Collect event kit" }
};

// Badge templates - Production ready templates
export const BADGE_TEMPLATES = {
  minimal: {
    name: "Minimal Clean",
    template: "minimal",
    backgroundColor: "#ffffff",
    accentColor: "#2563eb",
    fields: ["Name", "Email"],
    logoPosition: "top-center",
    qrPosition: "bottom-right",
    textColor: "#1f2937",
    borderColor: "#2563eb",
    borderWidth: "2px",
    headerBackground: "#ffffff",
    headerTextColor: "#1f2937",
    backgroundGradient: ["#ffffff", "#f8fafc"]
  },
  corporate: {
    name: "Corporate Style",
    template: "corporate",
    backgroundColor: "#f3f4f6",
    accentColor: "#1e3a8a",
    fields: ["Name", "Company", "Email"],
    logoPosition: "top-left",
    qrPosition: "bottom-center",
    textColor: "#1e293b",
    borderColor: "#1e3a8a",
    borderWidth: "3px",
    headerBackground: "#1e3a8a",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#f3f4f6", "#e5e7eb"]
  },
  modern: {
    name: "Modern Creative",
    template: "modern",
    backgroundColor: "#ffffff",
    accentColor: "#7c3aed",
    fields: ["Name", "Company", "Email", "Category"],
    logoPosition: "left-strip",
    qrPosition: "bottom-center",
    textColor: "#ffffff",
    borderColor: "#7c3aed",
    borderWidth: "2px",
    headerBackground: "#7c3aed",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#3b82f6", "#9333ea"]
  },
  // Additional professional templates
  professional: {
    name: "Professional Blue",
    template: "professional",
    backgroundColor: "#ffffff",
    accentColor: "#1e40af",
    fields: ["Name", "Company", "Email", "Title"],
    logoPosition: "top-center",
    qrPosition: "bottom-right",
    textColor: "#1e40af",
    borderColor: "#1e40af",
    borderWidth: "3px",
    headerBackground: "#1e40af",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#ffffff", "#f1f5f9"]
  },
  elegant: {
    name: "Elegant Gold",
    template: "elegant",
    backgroundColor: "#fefce8",
    accentColor: "#d97706",
    fields: ["Name", "Company", "Email"],
    logoPosition: "top-left",
    qrPosition: "bottom-center",
    textColor: "#92400e",
    borderColor: "#d97706",
    borderWidth: "2px",
    headerBackground: "#d97706",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#fefce8", "#fef3c7"]
  },
  tech: {
    name: "Tech Conference",
    template: "tech",
    backgroundColor: "#0f172a",
    accentColor: "#06b6d4",
    fields: ["Name", "Company", "Email", "Role"],
    logoPosition: "top-right",
    qrPosition: "bottom-left",
    textColor: "#06b6d4",
    borderColor: "#06b6d4",
    borderWidth: "2px",
    headerBackground: "#06b6d4",
    headerTextColor: "#0f172a",
    backgroundGradient: ["#0f172a", "#1e293b"]
  },
  startup: {
    name: "Startup Orange",
    template: "startup",
    backgroundColor: "#fff7ed",
    accentColor: "#ea580c",
    fields: ["Name", "Company", "Email"],
    logoPosition: "top-center",
    qrPosition: "bottom-right",
    textColor: "#ea580c",
    borderColor: "#ea580c",
    borderWidth: "2px",
    headerBackground: "#ea580c",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#fff7ed", "#fed7aa"]
  },
  healthcare: {
    name: "Healthcare Green",
    template: "healthcare",
    backgroundColor: "#f0fdf4",
    accentColor: "#16a34a",
    fields: ["Name", "Department", "Email", "ID"],
    logoPosition: "top-left",
    qrPosition: "bottom-center",
    textColor: "#16a34a",
    borderColor: "#16a34a",
    borderWidth: "3px",
    headerBackground: "#16a34a",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#f0fdf4", "#dcfce7"]
  },
  symposium: {
    name: "Symposium Professional",
    template: "symposium",
    backgroundColor: "#ffffff",
    accentColor: "#1e40af",
    fields: ["Name", "Company", "Booth", "Category"],
    logoPosition: "top-center",
    qrPosition: "bottom-center",
    textColor: "#1e293b",
    borderColor: "#e5e7eb",
    borderWidth: "1px",
    headerBackground: "#1e40af",
    headerTextColor: "#ffffff",
    backgroundGradient: ["#ffffff", "#f8fafc"],
    categoryBar: {
      enabled: true,
      backgroundColor: "#06b6d4",
      textColor: "#ffffff"
    }
  }
};

// Default user plan (for backward compatibility)
export const DEFAULT_USER_PLAN = {
  planId: "free",
  name: "Free Plan",
  description: "Basic features for small events",
  maxEvents: 3,
  maxRegistrations: 100,
  features: {
    qrScans: 50,
    teamMembers: 2,
    csvExport: true,
    customForms: true,
    badgeGeneration: true
  },
  price: 0,
  billingCycle: "monthly"
};

// Default payment settings
export const DEFAULT_PAYMENT_SETTINGS = {
  enabled: false,
  paymentMethods: ["bank_transfer"],
  currency: "INR",
  bankDetails: {
    accountName: "",
    accountNumber: "",
    bankName: "",
    routingNumber: "",
    swiftCode: "",
    iban: ""
  },
  instructions: "Please transfer the payment to the bank account details provided above."
};

// Default Form Schemas (minimal set for production)
export const DEFAULT_FORM_SCHEMAS = [
  {
    schemaId: "default",
    name: "Default Registration Form",
    description: "Standard registration form with essential fields",
    fields: [
      { type: "text", label: "Name", required: true, placeholder: "Enter your Name" },
      { type: "email", label: "Email", required: true, placeholder: "Enter your email address" },
      { type: "text", label: "Phone", required: false, placeholder: "Your phone number" },
      { type: "text", label: "Company", required: false, placeholder: "Your company name" }
    ],
    createdAt: "2024-01-01T00:00:00Z"
  }
];

// Toast notification types
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info'
};

// Event types
export const EVENT_TYPES = {
  FREE: 'free',
  PAID: 'paid'
};

// Registration statuses
export const REGISTRATION_STATUS = {
  REGISTERED: 'registered',
  PENDING_PAYMENT: 'pending_payment',
  PAID: 'paid',
  PAYMENT_FAILED: 'payment_failed',
  CANCELLED: 'cancelled'
};

// User roles
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  EDITOR: 'EDITOR',
  VIEWER: 'VIEWER'
};

// File upload limits
export const FILE_UPLOAD_LIMITS = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: {
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    spreadsheets: ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
  }
};

// Badge generation settings
export const BADGE_SETTINGS = {
  DEFAULT_SIZE: { width: 300, height: 200 },
  SUPPORTED_FORMATS: ['pdf', 'png', 'jpg'],
  QR_CODE_SIZE: 80,
  DEFAULT_FONT: 'Arial',
  DEFAULT_FONT_SIZE: 14
};

// Pagination defaults
export const PAGINATION_DEFAULTS = {
  PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100
};

// API timeout settings
export const API_TIMEOUTS = {
  DEFAULT: 30000, // 30 seconds
  UPLOAD: 120000, // 2 minutes
  DOWNLOAD: 60000 // 1 minute
};

// Form field types configuration
export const FORM_FIELD_TYPES = {
  text: {
    component: "input",
    inputType: "text",
    validation: "string"
  },
  email: {
    component: "input",
    inputType: "email",
    validation: "email"
  },
  textarea: {
    component: "textarea",
    validation: "string"
  },
  dropdown: {
    component: "select",
    validation: "string"
  },
  file: {
    component: "file",
    validation: "file"
  },
  date: {
    component: "input",
    inputType: "date",
    validation: "date"
  },
  number: {
    component: "input",
    inputType: "number",
    validation: "number"
  },
  list: {
    component: "list",
    validation: "array"
  }
};

// Legal identity shown on the policy pages. Use the exact name and address on the Cashfree/KYC account.
export const LEGAL_ENTITY = "NextWave SaaS OPC Private Limited";
export const REGISTERED_ADDRESS = "New Delhi 110094, India";
const SUPPORT_EMAIL = "info@nextwavesaas.tech";
const SUPPORT_WHATSAPP = "918217862472";
const SUPPORT_PHONE_DISPLAY = "+91 82178 62472";

// Compliance Pages Configuration - Production Ready
export const COMPLIANCE_PAGES = {
  aboutUs: {
    title: "About Us",
    slug: "about-us",
    description: "Learn more about EventEase and our mission",
    content: {
      hero: {
        title: "About EventEase",
        subtitle: "Transforming event management with innovative technology solutions"
      },
      sections: [
        {
          title: "Our Mission",
          content: "EventEase is dedicated to simplifying event management through cutting-edge technology. We empower event organizers with tools that streamline registration, enhance attendee experience, and provide valuable insights through advanced analytics."
        },
        {
          title: "What We Do",
          content: "We provide a comprehensive event management platform that includes dynamic registration forms, QR code generation, facial recognition check-ins, payment processing, and real-time analytics. Our platform serves conferences, workshops, seminars, and various corporate events."
        },
        {
          title: "Our Technology",
          content: "Built on modern web technologies with security and scalability at its core, EventEase offers cloud-based solutions that can handle events of any size. Our platform includes advanced features like facial recognition, multi-purpose QR scanning, and team collaboration tools."
        },
        {
          title: "Our Team",
          content: "We are a passionate team of developers, designers, and event management experts committed to delivering exceptional user experiences. Our diverse background in technology and event management allows us to understand and solve real-world challenges."
        }
      ],
      contact: {
        title: "Get In Touch",
        content: "Ready to transform your event management? Contact us to learn how EventEase can help streamline your next event.",
        whatsapp: "918217862472",
        email: "info@nextwavesaas.tech"
      }
    },
    lastUpdated: "2025-01-01"
  },
  privacyPolicy: {
    title: "Privacy Policy",
    slug: "privacy-policy",
    description: "How EventEase collects, uses, shares and protects personal data",
    content: {
      hero: {
        title: "Privacy Policy",
        subtitle: "How we handle the personal data of event organisers and attendees on EventEase."
      },
      lastUpdated: "October 17, 2026",
      sections: [
        {
          title: "Who we are",
          content: [
            `EventEase (event.nextwavesaas.tech) is an event registration and management platform operated by ${LEGAL_ENTITY}${REGISTERED_ADDRESS ? `, ${REGISTERED_ADDRESS}` : ""}.`,
            "Event organisers use EventEase to run registrations, payments, check-ins, badges and certificates for their events. Attendees use it to register for those events and manage their registrations.",
            "For attendee data collected through an organiser's registration form, the organiser decides what is collected and why, and EventEase processes it on the organiser's behalf. For organiser accounts and for running the platform itself, EventEase is responsible for the data."
          ]
        },
        {
          title: "Information we collect",
          content: [
            "Registration details: the answers you give in an event's registration form, such as name, email, mobile number, organisation, designation, category and any other fields the organiser adds, and any files you upload (for example ID proof or a photo).",
            "Payment details: the amount, payment method type, payment status and transaction or order reference. For offline payments, the transaction ID and payment screenshot you upload. We do not collect or store card numbers, CVV, UPI PINs or net banking passwords; these are entered only on our payment partner's secure pages.",
            "Check-in and event activity: QR code scans, check-ins for sessions, meals or kits, badge and certificate generation, and the device or scanner name that recorded a scan.",
            "Changes you make: when you view, edit or cancel your registration through the 'My registration' link, we record what changed, when, and the IP address and browser used, to keep an audit trail and prevent misuse.",
            "Facial images (optional): only if an event uses face check-in and you choose to provide a photo. We derive a facial template from it for matching at the venue.",
            "Organiser account data: name, email, phone, team members, and, for organisers who receive payouts, business details, PAN, GSTIN and bank account or UPI details needed by our payment partner to settle funds.",
            "Testimonials and content: testimonials you submit (name, role, message, rating) and photos or videos published on our website.",
            "Technical data: IP address, browser and device information, and logs needed to secure and operate the service. We use the browser's local storage to remember settings such as a scanner name, and essential cookies or tokens to keep you signed in."
          ]
        },
        {
          title: "How we use your information",
          content: [
            "To register you for events, confirm your registration and send your QR code, e-ticket, receipt or invoice, badge and certificate.",
            "To collect payments, apply platform or convenience fees, coupons and invite-link waivers, confirm payments automatically, and settle funds to event organisers.",
            "To let you view, update or cancel your registration, including sending one-time verification codes to your email.",
            "To run check-ins and attendance at the venue, including optional face check-in.",
            "To send service messages about the event by email and, where the organiser enables it, WhatsApp. Organisers may also send event-related announcements to registered attendees.",
            "To provide organisers with registration lists, exports and analytics for their own events.",
            "To prevent fraud, secure the platform, resolve disputes and meet legal, tax and accounting obligations."
          ]
        },
        {
          title: "How payments are processed",
          content: [
            "Online payments are processed by Cashfree Payments India Private Limited, a payment aggregator authorised by the Reserve Bank of India. When you pay, you are taken to Cashfree's secure checkout; card, UPI and bank credentials are handled by Cashfree and the issuing bank, not by EventEase.",
            "We share with Cashfree only what is needed to process the payment: the order amount, an order reference, and your name, email and mobile number.",
            "When an event's payments are settled to the organiser, Cashfree splits the amount and transfers the organiser's share to the organiser's verified bank account; the platform fee, if any, is retained by EventEase.",
            "For offline payments (bank transfer or UPI to the organiser), the transaction ID and screenshot you upload are shared with the event organiser, who verifies and approves the payment.",
            "Payment records are kept as required for accounting, tax and dispute resolution."
          ]
        },
        {
          title: "Who we share information with",
          content: [
            "Event organisers: the organiser of the event you register for receives your registration details, payment status, uploaded documents and check-in history for that event.",
            "Payment partner: Cashfree Payments, as described above.",
            "Service providers who help us run the platform, under confidentiality obligations: cloud hosting and storage (Amazon Web Services, India region), email delivery, WhatsApp messaging providers, and face-matching services where an event uses face check-in.",
            "Public pages you choose to appear on: approved testimonials, published gallery photos and videos, and event listings on our website. Shared live-statistics links show only aggregate counts, not personal data.",
            "Authorities, when required by law, court order or to protect rights, safety and prevent fraud.",
            "We do not sell your personal data."
          ]
        },
        {
          title: "Facial data",
          content: [
            "Face check-in is optional and used only if the event offers it and you provide a photo.",
            "Facial templates are used only to identify you at check-in for that event, are stored encrypted, and are not shared except with the face-matching service that performs the comparison.",
            "You can ask the organiser or us to delete your facial data at any time; you can still check in using your QR code."
          ]
        },
        {
          title: "Data security",
          content: [
            "Data is encrypted in transit (HTTPS) and stored on secured cloud infrastructure with access controls.",
            "Access to registration data is limited to the event's organiser team and our authorised staff who need it to provide support.",
            "'My registration' links and verification codes are private to the attendee; please do not share them.",
            "No system is completely secure. If we become aware of a breach affecting your data, we will notify you and the authorities as required by law."
          ]
        },
        {
          title: "Data retention",
          content: [
            "Registration and check-in data is kept for as long as the organiser keeps the event on EventEase and as needed for the organiser's records, after which it is deleted or anonymised.",
            "Payment, invoice and audit records are retained for the period required by tax and accounting laws (generally up to 8 years).",
            "Facial data is deleted when the event's data is deleted, or earlier on request.",
            "Organiser account data is kept while the account is active and for a reasonable period after closure."
          ]
        },
        {
          title: "Your rights",
          content: [
            "Under the Digital Personal Data Protection Act, 2023 and other applicable law, you may ask to access, correct or erase your personal data, withdraw consent, and nominate someone to exercise your rights.",
            "You can view and correct most registration details yourself through the 'My registration' link sent with your confirmation, until the organiser's change deadline.",
            "For requests about a specific event's data, you can contact the organiser directly or write to us and we will coordinate with the organiser.",
            "Withdrawing consent or deleting data may mean you can no longer attend or check in to an event that relies on it."
          ]
        },
        {
          title: "Children",
          content: [
            "EventEase is not directed at children. Registrations of persons under 18 should be made by, or with the consent of, a parent or guardian, as required by the event organiser and applicable law."
          ]
        },
        {
          title: "Changes to this policy",
          content: [
            "We may update this policy as our services change. The 'Last updated' date shows the latest version; significant changes will be highlighted on the website."
          ]
        },
        {
          title: "Grievance officer",
          content: [
            `For privacy questions, requests or complaints, contact our Grievance Officer at ${SUPPORT_EMAIL} or WhatsApp ${SUPPORT_PHONE_DISPLAY}. We aim to acknowledge within 48 hours and resolve within 30 days.`
          ]
        }
      ],
      contact: {
        title: "Contact Us",
        content: "Questions about this Privacy Policy or your personal data:",
        email: SUPPORT_EMAIL,
        whatsapp: SUPPORT_WHATSAPP
      }
    }
  },
  termsOfService: {
    title: "Terms and Conditions",
    slug: "terms-of-service",
    description: "Terms for organisers and attendees using EventEase",
    content: {
      hero: {
        title: "Terms and Conditions",
        subtitle: "Please read these terms before registering for an event or using EventEase."
      },
      lastUpdated: "October 17, 2026",
      sections: [
        {
          title: "About these terms",
          content: [
            `These terms govern the use of EventEase (event.nextwavesaas.tech), operated by ${LEGAL_ENTITY}, ${REGISTERED_ADDRESS} ("EventEase", "we", "us"), by event organisers ("Organisers") and by people who register for events ("Attendees").`,
            "By creating an account, registering for an event or making a payment on EventEase, you agree to these terms, our Privacy Policy and our Refund & Cancellation Policy."
          ]
        },
        {
          title: "Our role",
          content: [
            "EventEase is a technology platform. Each event listed or sold on EventEase is organised by its Organiser, who is solely responsible for the event: its content, schedule, venue, pricing, eligibility, admission rules, conduct and any changes or cancellation.",
            "EventEase is not the organiser of events unless expressly stated, and does not guarantee that an event will take place as described.",
            "Organisers receive payments for their events through EventEase and our payment partner; EventEase may retain a platform fee as described below."
          ]
        },
        {
          title: "Registration",
          content: [
            "Attendees must provide accurate and complete information. A registration is personal to the named attendee unless the Organiser allows transfers.",
            "A registration is confirmed when you receive a confirmation with your registration ID and QR code. For paid events this happens after successful payment, or after the Organiser approves an offline payment.",
            "Organisers may set eligibility criteria, capacity limits, registration deadlines, approval steps and invite-only or free invite links, and may reject or cancel registrations that do not meet their rules or that contain false information.",
            "On-spot and group registrations made at the venue are subject to the same terms; the person making a group registration confirms they are authorised to register the people named.",
            "Your QR code is your entry pass. Keep it safe; anyone presenting it may be checked in."
          ]
        },
        {
          title: "Prices and payments",
          content: [
            "Prices are set by the Organiser and shown in Indian Rupees (INR) before you pay. Prices may vary by category, option, coupon or date (for example early-bird pricing); the price that applies is the one shown at checkout.",
            "Some events add a convenience or platform fee, which is shown separately before payment.",
            "Online payments are processed by Cashfree Payments using cards, UPI, net banking, wallets and other methods offered at checkout. Your payment is subject to Cashfree's and your bank's terms.",
            "A registration paid online is confirmed automatically once the payment is confirmed by the payment partner. If money is debited but your registration is not confirmed, do not pay again; it is usually confirmed within minutes, or the amount is refunded as described in the Refund & Cancellation Policy.",
            "For offline payments, you pay the Organiser directly using the bank or UPI details shown and upload the transaction ID and screenshot. The registration is confirmed only after the Organiser verifies the payment.",
            "Coupons and free invite links are issued by Organisers, have their own limits and expiry, cannot be exchanged for cash, and may be withdrawn if misused.",
            "Receipts or GST invoices, where applicable, are issued by or on behalf of the Organiser for the registration amount."
          ]
        },
        {
          title: "Refunds and cancellations",
          content: [
            "Refunds and cancellations are decided by the Organiser of each event, according to the Organiser's own refund policy for that event. Please check the event page or contact the Organiser before registering.",
            "Cancelling a registration (by the Attendee through 'My registration', or by the Organiser) does not by itself trigger a refund; any refund is processed only if the Organiser approves it.",
            "The platform or convenience fee is non-refundable, except where a payment failed, was charged twice, or the event is cancelled and the Organiser approves a full refund, or where required by law.",
            "Full details are in our Refund & Cancellation Policy."
          ]
        },
        {
          title: "Changes to your registration",
          content: [
            "Where the Organiser allows it, you can view, update or cancel your registration through the 'My registration' link until the Organiser's deadline.",
            "If a change increases the price (for example upgrading a pass), you must pay the difference and the change takes effect only after the Organiser approves the payment.",
            "Your email address cannot be changed online, as it identifies you."
          ]
        },
        {
          title: "Delivery of tickets",
          content: [
            "EventEase delivers services electronically. Your confirmation, QR code / e-ticket and, where applicable, receipt are sent to the email address you register with, usually within minutes of confirmation, and can be downloaded again from 'My registration'.",
            "Badges are printed at the venue and certificates, where offered, are emailed after the event. Nothing is shipped physically. See our Shipping & Delivery Policy."
          ]
        },
        {
          title: "Organiser obligations",
          content: [
            "Organisers must provide accurate event information, honour confirmed registrations, publish a clear refund policy for their events and handle attendee queries, refunds and complaints about their events.",
            "Organisers receiving online payouts must complete our payment partner's verification (KYC) with accurate business and bank details, and are responsible for their own taxes, invoices and statutory compliance.",
            "Organisers are responsible for having a lawful basis and any consents needed for the data they collect through their registration forms, and must use attendee data only for their event.",
            "Organisers are responsible for chargebacks and disputes relating to their events. EventEase may recover refunded amounts, chargebacks and related charges from the Organiser's future settlements.",
            "Organiser subscriptions to EventEase plans are billed in advance and are non-refundable except as required by law or stated in the plan."
          ]
        },
        {
          title: "Acceptable use",
          content: [
            "Do not provide false information, impersonate others, resell or duplicate QR codes, or attempt to bypass payments, coupons or invite-link limits.",
            "Do not upload unlawful, infringing or harmful content, attempt to access other people's data, or interfere with the security or operation of the platform.",
            "We may suspend or cancel accounts or registrations that breach these terms, in consultation with the Organiser where relevant."
          ]
        },
        {
          title: "Content and intellectual property",
          content: [
            "EventEase owns the platform and its software. Organisers keep the rights to their event content and grant us a licence to host and display it to run the service.",
            "By submitting a testimonial or content for our website, you allow us to publish it on our website and marketing material with your name and role."
          ]
        },
        {
          title: "Limitation of liability",
          content: [
            "The platform is provided on an 'as is' basis. We work to keep it available but do not guarantee uninterrupted service.",
            "EventEase is not liable for the conduct of any event, for an Organiser's acts or omissions, or for indirect or consequential losses.",
            "To the extent permitted by law, our total liability to an Attendee for any registration is limited to the platform fee received by us for that registration, and to an Organiser to the fees paid to us in the 12 months before the claim."
          ]
        },
        {
          title: "Governing law and grievances",
          content: [
            "These terms are governed by the laws of India. Courts at New Delhi have jurisdiction, subject to any consumer protection rights you have.",
            `For complaints, contact ${SUPPORT_EMAIL} or WhatsApp ${SUPPORT_PHONE_DISPLAY}. We acknowledge within 48 hours and aim to resolve within 30 days. Complaints about an event itself are forwarded to its Organiser.`,
            "We may update these terms; the 'Last updated' date shows the current version. Continued use after changes means you accept them."
          ]
        }
      ],
      contact: {
        title: "Questions About These Terms",
        content: "If you have questions about these terms, please contact us:",
        email: SUPPORT_EMAIL,
        whatsapp: SUPPORT_WHATSAPP
      }
    }
  },
  refundPolicy: {
    title: "Refund & Cancellation Policy",
    slug: "refund-policy",
    description: "How refunds and cancellations work for events on EventEase",
    content: {
      hero: {
        title: "Refund & Cancellation Policy",
        subtitle: "Refunds for each event are decided by that event's organiser."
      },
      lastUpdated: "October 17, 2026",
      sections: [
        {
          title: "Refunds are set by the event organiser",
          content: [
            "Every event on EventEase is run by its organiser, who decides whether refunds are allowed, until when, and how much is refunded (for example full, partial or none, or credit for a future event).",
            "Check the event's page or confirmation email for the organiser's refund terms, or contact the organiser before registering. If an event shows no refund terms, registrations are non-refundable unless the organiser agrees otherwise or the law requires it."
          ]
        },
        {
          title: "Cancelling your registration",
          content: [
            "If the organiser allows it, you can cancel through the 'My registration' link in your confirmation email until the organiser's deadline. Otherwise, contact the organiser.",
            "Cancelling releases your place and deactivates your QR code. It does not automatically issue a refund; a refund is made only if the organiser's refund terms allow it and the organiser approves it."
          ]
        },
        {
          title: "If the event is cancelled or changed",
          content: [
            "If an organiser cancels an event, the organiser is responsible for refunding registrations or offering an alternative, as stated in their refund terms.",
            "If the date, venue or programme changes significantly, contact the organiser for the options available."
          ]
        },
        {
          title: "Platform and convenience fees",
          content: [
            "Any platform or convenience fee shown at checkout is non-refundable, except when the payment failed, you were charged more than once, or the event is cancelled and the organiser approves a full refund, or where required by law."
          ]
        },
        {
          title: "Failed and duplicate payments",
          content: [
            "If money was debited but your registration was not confirmed, it is usually confirmed automatically within a few minutes. If it is not, the payment is refunded to the original payment method.",
            "Duplicate charges for the same registration are refunded in full once identified. Write to us with your registration ID or payment reference if you notice one."
          ]
        },
        {
          title: "How and when refunds are paid",
          content: [
            "Approved refunds for online payments are made to the original payment method through our payment partner, Cashfree Payments. They are usually initiated within 7 working days of approval and reach your account within 5–7 working days after that, depending on your bank.",
            "Refunds for offline payments (bank transfer or UPI paid to the organiser) are made by the organiser directly to the account you paid from."
          ]
        },
        {
          title: "Need help?",
          content: [
            `For refunds, contact the event's organiser first. If you cannot reach them, or for failed or duplicate payments, write to ${SUPPORT_EMAIL} or WhatsApp ${SUPPORT_PHONE_DISPLAY} with your registration ID and payment reference.`
          ]
        }
      ],
      contact: {
        title: "Contact Us",
        content: "Questions about a refund or payment:",
        email: SUPPORT_EMAIL,
        whatsapp: SUPPORT_WHATSAPP
      }
    }
  },
  shippingPolicy: {
    title: "Shipping & Delivery Policy",
    slug: "shipping-policy",
    description: "How tickets, receipts and certificates are delivered",
    content: {
      hero: {
        title: "Shipping & Delivery Policy",
        subtitle: "EventEase delivers registrations electronically. Nothing is shipped physically."
      },
      lastUpdated: "October 17, 2026",
      sections: [
        {
          title: "What you receive",
          content: [
            "After your registration is confirmed you receive an email with your registration ID and QR code (your e-ticket) and, for paid registrations, a receipt or invoice where applicable.",
            "Online payments are usually confirmed within minutes, so the email normally arrives within minutes of payment. Registrations that need the organiser's approval (for example offline payments) are delivered once approved."
          ]
        },
        {
          title: "If you don't receive it",
          content: [
            "Check your spam or promotions folder, and that the email address you entered is correct.",
            "You can open your registration again at any time from the 'My registration' page using your email and a one-time code, and download your QR code and receipt there.",
            `Still missing? Contact the organiser or write to ${SUPPORT_EMAIL} with your registration ID.`
          ]
        },
        {
          title: "Badges and certificates",
          content: [
            "Event badges are printed and handed over at the venue when you check in with your QR code.",
            "Where the organiser offers certificates, they are emailed after the event."
          ]
        },
        {
          title: "No physical shipping",
          content: [
            "EventEase does not ship physical goods. Any physical kits or materials are handed out by the organiser at the event."
          ]
        }
      ],
      contact: {
        title: "Contact Us",
        content: "Questions about delivery of your ticket:",
        email: SUPPORT_EMAIL,
        whatsapp: SUPPORT_WHATSAPP
      }
    }
  },
  contactUs: {
    title: "Contact Us",
    slug: "contact-us",
    description: "Get in touch with EventEase team for support and inquiries",
    content: {
      hero: {
        title: "Contact Us",
        subtitle: "We're here to help. Reach out for support, questions, or feedback."
      },
      sections: [
        {
          title: "Get Support",
          content: "Our support team is ready to help you with any questions about EventEase. Whether you need technical assistance, have billing questions, or want to learn more about our features, we're here for you."
        },
        {
          title: "Business Inquiries",
          content: "Interested in enterprise solutions or partnerships? We'd love to discuss how EventEase can meet your organization's event management needs."
        },
        {
          title: "Technical Support",
          content: "Experiencing technical issues? Our technical team provides comprehensive support to ensure your events run smoothly."
        }
      ],
      contactMethods: [
        {
          title: "WhatsApp Support",
          description: "Quick response via WhatsApp",
          value: "+918217862472",
          link: "https://wa.me/918217862472",
          icon: "message-circle",
          available: "24/7 Quick Response"
        },
        {
          title: "Call Support",
          description: "Quick response via call",
          value: "+918217862472",
          link: "https://wa.me/918217862472",
          icon: "message-circle",
          available: "24/7 Quick Response"
        },
        {
          title: "Email Support",
          description: "Comprehensive support via email",
          value: "info@nextwavesaas.tech", 
          link: "mailto:info@nextwavesaas.tech",
          icon: "mail",
          available: "Response within 24 hours"
        },
        {
          title: "Technical Issues",
          description: "For technical problems and bugs",
          value: "info@nextwavesaas.tech",
          link: "mailto:info@nextwavesaas.tech", 
          icon: "settings",
          available: "Priority technical support"
        },
        {
          title: "Business & Sales",
          description: "Enterprise solutions and partnerships",
          value: "info@nextwavesaas.tech",
          link: "mailto:info@nextwavesaas.tech",
          icon: "briefcase", 
          available: "Business consultation"
        },
        {
          title: "Registered Office",
          description: LEGAL_ENTITY,
          value: REGISTERED_ADDRESS,
          link: "https://maps.google.com/?q=New+Delhi+110094",
          icon: "map-pin",
          available: "Correspondence by email preferred"
        }
      ],
      features: [
        {
          title: "24/7 WhatsApp Support",
          description: "Get instant help through our WhatsApp channel"
        },
        {
          title: "Expert Technical Team", 
          description: "Experienced professionals ready to solve complex issues"
        },
        {
          title: "Comprehensive Documentation",
          description: "Detailed guides and tutorials for all features"
        },
        {
          title: "Community Support",
          description: "Connect with other EventEase users and share best practices"
        }
      ]
    }
  }
};
